import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import { Upload, TrendingUp, Users, FileText, Trophy, ArrowRight, Loader2, Repeat, BarChart3, Dumbbell, Clock } from 'lucide-react';

function daysLeft(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

const SEVERITY_BADGE_CLASS = {
  High: 'bg-red-50 text-red-700 border-red-200',
  Medium: 'bg-amber-50 text-amber-700 border-amber-200',
  Low: 'bg-emerald-50 text-emerald-700 border-emerald-200'
};

const STATUS_BADGE_CLASS = {
  Active: 'bg-red-50 text-red-700 border-red-200',
  Improving: 'bg-amber-50 text-amber-700 border-amber-200',
  Resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200'
};

const QUALITY_BADGE_CLASS = {
  High: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Medium: 'bg-amber-50 text-amber-700 border-amber-200',
  Low: 'bg-red-50 text-red-700 border-red-200',
  Excellent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Good: 'bg-blue-50 text-blue-700 border-blue-200',
  Limited: 'bg-amber-50 text-amber-700 border-amber-200',
  Minimal: 'bg-red-50 text-red-700 border-red-200'
};



export function qualityLabel(report) {
  if (!report) return null;
  if (typeof report.input_quality_label === 'string' && report.input_quality_label.trim()) {
    return report.input_quality_label.trim();
  }

  const raw = report.input_quality;
  if (raw == null) return null;

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object') {
          const label = parsed.label || parsed.level || parsed.quality || parsed.status;
          return typeof label === 'string' && label.trim() ? label.trim() : null;
        }
        return typeof parsed === 'string' && parsed.trim() ? parsed.trim() : null;
      } catch (_e) {
        return null;
      }
    }
    return trimmed;
  }

  if (typeof raw === 'object') {
    const label = raw.label || raw.level || raw.quality || raw.status;
    return typeof label === 'string' && label.trim() ? label.trim() : null;
  }

  return null;
}

function Badge({ text, classMap }) {
  const cls = (classMap && classMap[text]) || 'bg-gray-50 text-gray-600 border-gray-200';
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${cls}`}>
      {text}
    </span>
  );
}

export default function Dashboard() {
  const { org } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // ── Team filter helpers ────────────────────────────────────────────────

    const justSignedUp = Boolean(location.state?.justSignedUp);
  const [matches, setMatches] = useState([]);
  const [reports, setReports] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(() => {
    try { return localStorage.getItem('tactmark_selected_team') || null; } catch { return null; }
  });
  const handleTeamSwitch = (teamId) => {
    setSelectedTeamId(teamId);
    try { localStorage.setItem('tactmark_selected_team', teamId || ''); } catch {}
  };
  const effectiveSelectedTeamId = selectedTeamId && teams.some(tm => tm.id === selectedTeamId) ? selectedTeamId : null;
  const teamFilteredMatches = effectiveSelectedTeamId
    ? matches.filter(m => m.team_id === effectiveSelectedTeamId || (!m.team_id && teams.length <= 1))
    : matches;
  const teamFilteredReports = effectiveSelectedTeamId
    ? reports.filter(r => r.team_id === effectiveSelectedTeamId || (!r.team_id && teams.length <= 1))
    : reports;
  const [players, setPlayers] = useState([]);
  const [patterns, setPatterns] = useState(null);
  const [patternsLoading, setPatternsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [coachingFindings, setCoachingFindings] = useState([]);
  const [coachingDecisions, setCoachingDecisions] = useState([]);

  useEffect(() => {
    Promise.all([
      api.getMatches(),
      api.getReports(),
      api.getTeams(),
      api.getPlayers(),
      api.getCoachingFindings().catch(() => ({ findings: [] })),
      api.getCoachingDecisions().catch(() => ({ decisions: [] })),
    ])
      .then(([m, r, t, p, findingsRes, decisionsRes]) => {
        setMatches(m.matches || []);
        setReports(r.reports || []);
        const loadedTeams = t.teams || [];
        setTeams(loadedTeams);
        // Auto-select first team if nothing saved
        setSelectedTeamId(prev => {
          const valid = prev && loadedTeams.some(tm => tm.id === prev);
          const chosen = valid ? prev : (loadedTeams[0]?.id || null);
          try { localStorage.setItem('tactmark_selected_team', chosen || ''); } catch {}
          return chosen;
        });
        setPlayers(p.players || []);
        setCoachingFindings(findingsRes.findings || []);
        setCoachingDecisions(decisionsRes.decisions || []);
        setPatternsLoading(false);
      })
      .finally(() => setLoading(false));
  }, []);

  // Refetch patterns whenever the selected team changes
  useEffect(() => {
    if (!effectiveSelectedTeamId) {
      setPatterns(null);
      return;
    }
    let cancelled = false;
    setPatternsLoading(true);
    api.getPatterns(effectiveSelectedTeamId)
      .then((res) => { if (!cancelled) setPatterns(res); })
      .catch(() => { if (!cancelled) setPatterns(null); })
      .finally(() => { if (!cancelled) setPatternsLoading(false); });
    return () => { cancelled = true; };
  }, [effectiveSelectedTeamId]);

  const recentMatches = teamFilteredMatches.slice(0, 5);
  const recurringPatterns = teamFilteredReports
    .flatMap((r) => (r.analysis_engine_findings?.patterns || []))
    .filter((p) => p.severity === 'recurring');
  const latestReport = teamFilteredReports[0];
  const trialDaysLeft = org?.status === 'Trial' ? daysLeft(org.trialEndsAt) : null;

  // ── Pattern Library data ──
  const patternData = patterns?.patterns || patterns || {};
  // Support both camelCase (engine output) and snake_case (stored fields)
  const recurringIssues = patternData.recurringIssues || patternData.recurring_issues || [];
  const trainingFocusHistory = patternData.trainingFocusHistory || patternData.training_focus_history || [];
  // playerRecurringIssues reserved for future player-level pattern UI
  const recentReportsStrip = (teamFilteredReports || []).slice(0, 5);
  const hasPatternData = recurringIssues.length > 0 || trainingFocusHistory.length > 0;

  // Deduplicate training focus history (by area/title), most recent first
  const dedupedTrainingFocus = (() => {
    const seen = new Set();
    const result = [];
    for (const item of trainingFocusHistory) {
      const key = (typeof item === 'string') ? item : (item.area || item.title || JSON.stringify(item));
      if (!seen.has(key)) {
        seen.add(key);
        result.push(item);
      }
    }
    return result;
  })();

  if (loading) {
    return <div className="text-center py-24"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-500" /></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy-900">{justSignedUp ? `Welcome to Tactmark${org?.name ? `, ${org.name}` : ''}` : 'Welcome back'}</h1>
          <p className="text-gray-500 mt-1">
            {justSignedUp ? "Your trial has started let's log your first match." : "Here's your team performance overview"}
          </p>
          {/* Team Switcher */}
          {teams.length > 1 && (
            <div className="flex items-center gap-3 mb-6">
              <span className="text-sm font-medium text-navy-600">Viewing:</span>
              <div className="flex gap-2 flex-wrap">
                {teams.map(t => (
                  <button
                    key={t.id}
                    onClick={() => handleTeamSwitch(t.id)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      selectedTeamId === t.id
                        ? 'bg-navy-900 text-white border-navy-900'
                        : 'bg-white text-navy-700 border-navy-200 hover:border-navy-400'
                    }`}
                  >
                    {t.name}
                  </button>
                ))}
                <button
                  onClick={() => handleTeamSwitch(null)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    !selectedTeamId
                      ? 'bg-navy-900 text-white border-navy-900'
                      : 'bg-white text-navy-700 border-navy-200 hover:border-navy-400'
                  }`}
                >
                  All teams
                </button>
              </div>
            </div>
          )}

        </div>
        {trialDaysLeft !== null && (
          <div className="bg-navy-50 border border-navy-200 rounded-lg px-4 py-2 text-sm text-navy-900">
            {trialDaysLeft > 0 ? `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left in your trial` : 'Your trial has ended'}
          </div>
        )}
        {org?.status === 'Trial expired' && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-700">
            Trial expired: <Link to="/subscription" className="underline">upgrade to keep generating reports</Link>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={FileText} label="Matches" value={teamFilteredMatches.length} color="text-navy-600" />
        <StatCard icon={TrendingUp} label="Reports Generated" value={teamFilteredReports.length} color="text-navy-600" />
        <StatCard icon={Trophy} label="Teams" value={teams.length} color="text-navy-600" />
        <StatCard icon={Users} label="Players Tracked" value={players.length} color="text-navy-600" />
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <Link to="/upload" className="flex-1 bg-navy-900 hover:bg-navy-800 text-white rounded-2xl px-6 py-5 flex items-center justify-between transition-colors">
          <div className="flex items-center gap-3">
            <Upload className="w-6 h-6" />
            <div>
              <p className="font-semibold">New Analysis</p>
              <p className="text-navy-200 text-sm">Generate a coaching report</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>

      {recurringPatterns.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Repeat className="w-5 h-5 text-navy-600" />
            <h2 className="text-navy-900 font-semibold">Coaching Decision Alerts</h2>
          </div>
          <ul className="space-y-2">
            {recurringPatterns.slice(0, 4).map((p, i) => (
              <li key={i} className="text-gray-700 text-sm flex gap-2">
                <span className="text-navy-600 mt-0.5">•</span> {p.label} ({p.evidence})
              </li>
            ))}
          </ul>
        </div>
      )}

      {(coachingFindings.length > 0 || coachingDecisions.length > 0) && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-navy-600" />
              <h2 className="text-navy-900 font-semibold">Decision Memory</h2>
            </div>
            <Link to="/memory" className="text-sm text-navy-700 hover:underline">Search memory</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Active / proposed findings</p>
              <ul className="space-y-2">
                {(effectiveSelectedTeamId
                  ? coachingFindings.filter((f) => !f.teamId || f.teamId === effectiveSelectedTeamId)
                  : coachingFindings
                ).filter((f) => !['rejected'].includes(f.staffStatus)).slice(0, 5).map((f) => (
                  <li key={f.id} className="text-sm text-gray-700">
                    <Link to={f.matchId ? `/analysis/${f.matchId}` : '/memory'} className="text-navy-900 font-medium hover:underline">
                      {f.title}
                    </Link>
                    <span className="text-gray-400 text-xs ml-2">{f.staffStatus}{f.recurrenceClass ? ` · ${f.recurrenceClass}` : ''}</span>
                  </li>
                ))}
                {!coachingFindings.length && <li className="text-sm text-gray-400">No findings yet.</li>}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Decisions awaiting review / monitoring</p>
              <ul className="space-y-2">
                {(effectiveSelectedTeamId
                  ? coachingDecisions.filter((d) => !d.teamId || d.teamId === effectiveSelectedTeamId)
                  : coachingDecisions
                ).filter((d) => !['Resolved'].includes(d.status)).slice(0, 5).map((d) => (
                  <li key={d.id} className="text-sm text-gray-700">
                    <span className="text-navy-900 font-medium">{d.decision}</span>
                    <span className="text-gray-400 text-xs ml-2">{d.status}</span>
                  </li>
                ))}
                {!coachingDecisions.length && <li className="text-sm text-gray-400">No decisions yet — confirm findings on a report.</li>}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div>
        <h2 className="text-navy-900 font-semibold mb-4">Recent Matches</h2>
        {recentMatches.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
            <FileText className="text-gray-400 w-12 h-12 mx-auto mb-4" />
            <p className="text-gray-500">No matches yet. Start your first analysis!</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-5 py-3 text-gray-500 font-medium">Match</th>
                  <th className="text-left px-5 py-3 text-gray-500 font-medium hidden sm:table-cell">Date</th>
                  <th className="text-left px-5 py-3 text-gray-500 font-medium hidden md:table-cell">Focus</th>
                  <th className="text-left px-5 py-3 text-gray-500 font-medium">Report</th>
                </tr>
              </thead>
              <tbody>
                {recentMatches.map((match, i) => {
                  const matchReport = reports.find(r => r.match_id === match.id);
                  // Every saved match, with or without a report, resolves to its
                  // analysis page by match id. We NEVER route an existing match to
                  // /upload (that page is for new analyses and would risk a duplicate).
                  const analysisHref = `/analysis/${match.id}`;
                  const goToAnalysis = () => navigate(analysisHref);
                  return (
                    <tr
                      key={match.id}
                      data-testid={`match-row-${match.id}`}
                      onClick={goToAnalysis}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToAnalysis(); } }}
                      role="link"
                      tabIndex={0}
                      aria-label={`Open analysis for ${match.our_team_name || match.team_name || 'Our Team'} vs ${match.opponent_name || match.opponent || 'Opponent'}`}
                      className={`cursor-pointer border-b border-gray-50 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors ${i === recentMatches.length - 1 ? 'border-0' : ''}`}
                    >
                      <td className="px-5 py-4">
                        <Link
                          to={analysisHref}
                          data-testid={`match-title-${match.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-navy-900 hover:text-navy-600 hover:underline"
                        >
                          {match.our_team_name || match.team_name || 'Our Team'} vs {match.opponent_name || match.opponent || 'Opponent'}
                        </Link>
                        <p className="text-gray-400 text-xs mt-0.5">{match.venue || ''}</p>
                      </td>
                      <td className="px-5 py-4 text-gray-500 hidden sm:table-cell">
                        {match.match_date ? new Date(match.match_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        {match.tactical_focus ? <Badge text={match.tactical_focus} classMap={{}} /> : <span className="text-gray-300"></span>}
                      </td>
                      <td className="px-5 py-4">
                        {matchReport ? (
                          <Link
                            to={analysisHref}
                            data-testid={`view-report-${match.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-navy-600 hover:text-navy-900 font-medium inline-flex items-center gap-1"
                          >
                            View report <ArrowRight className="w-3 h-3" />
                          </Link>
                        ) : (
                          <Link
                            to={analysisHref}
                            data-testid={`generate-report-${match.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-navy-600 hover:text-navy-900 text-xs font-medium inline-flex items-center gap-1"
                          >
                            Generate report <ArrowRight className="w-3 h-3" />
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {teamFilteredMatches.length > 5 && (
          <div className="text-center mt-3">
            <Link to="/matches" className="text-navy-600 text-sm hover:underline">View all {teamFilteredMatches.length} matches</Link>
          </div>
        )}
      </div>

      {latestReport && (
        <div className="mt-8">
          <h2 className="text-navy-900 font-semibold mb-4">Latest Report</h2>
          <Link
            to={`/analysis/${latestReport.match_id}`}
            className="block bg-white border border-gray-200 rounded-2xl p-5 hover:border-navy-300 transition-colors"
          >
            <p className="text-navy-900 font-medium">{latestReport.report_title}</p>
            <p className="text-gray-500 text-sm mt-1">{latestReport.verdict_summary}</p>
          </Link>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* ── Team Pattern Library ── */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <div className="mt-12">
        <div className="flex items-center gap-3 mb-6">
          <BarChart3 className="w-6 h-6 text-navy-600" />
          <div>
            <h2 className="text-2xl font-bold text-navy-900">Team Intelligence</h2>
            <p className="text-gray-500 text-sm mt-0.5">Recurring patterns, training history, and report quick-access</p>
          </div>
        </div>

        {patternsLoading ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
          </div>
        ) : !hasPatternData ? (
          /* ── Empty state ── */
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
            <BarChart3 className="text-gray-300 w-12 h-12 mx-auto mb-4" />
            <p className="text-gray-500 font-medium mb-1">No patterns yet</p>
            <p className="text-gray-400 text-sm">Patterns build up after your first few analyses. Come back after 3 matches.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* ── Recurring Issues ── */}
            {recurringIssues.length > 0 && (
              <div>
                <h3 className="text-navy-900 font-semibold mb-4 flex items-center gap-2">
                  <Repeat className="w-4 h-4 text-navy-600" /> Recurring Issues
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {recurringIssues.map((issue, i) => (
                    <div key={i} className="bg-white border border-gray-200 rounded-2xl p-5">
                      <p className="text-navy-900 font-medium text-sm mb-2">{issue.issue || issue.name || ''}</p>
                      <div className="flex items-center gap-2 mb-2">
                        {issue.appearances && (
                          <span className="text-gray-500 text-xs">{issue.appearances}</span>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {issue.severity && <Badge text={issue.severity} classMap={SEVERITY_BADGE_CLASS} />}
                        {issue.status && <Badge text={issue.status} classMap={STATUS_BADGE_CLASS} />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Training Focus History ── */}
            {dedupedTrainingFocus.length > 0 && (
              <div>
                <h3 className="text-navy-900 font-semibold mb-4 flex items-center gap-2">
                  <Dumbbell className="w-4 h-4 text-navy-600" /> Training Focus History
                </h3>
                <div className="flex flex-wrap gap-2">
                  {dedupedTrainingFocus.map((item, i) => {
                    const label = typeof item === 'string' ? item : (item.area || item.title || '');
                    const date = typeof item === 'object' ? (item.date || item.match_date) : null;
                    return (
                      <div key={i} className="bg-white border border-gray-200 rounded-lg px-4 py-2.5 flex items-center gap-2">
                        <span className="text-navy-900 text-sm font-medium">{label}</span>
                        {date && (
                          <span className="flex items-center gap-1 text-gray-400 text-xs">
                            <Clock className="w-3 h-3" /> {date}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Previous Reports Quick-Access Strip ── */}
            {recentReportsStrip.length > 0 && (
              <div>
                <h3 className="text-navy-900 font-semibold mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-navy-600" /> Previous Reports
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  {recentReportsStrip.map((rep) => {
                    const qualityLevel = qualityLabel(rep);
                    const matchName = rep.report_title || `Match ${rep.match_id}`;
                    return (
                      <Link
                        key={rep.id || rep.match_id}
                        to={`/analysis/${rep.match_id}`}
                        className="bg-white border border-gray-200 rounded-2xl p-4 hover:border-navy-300 transition-colors flex flex-col gap-2"
                      >
                        <p className="text-navy-900 font-medium text-sm leading-snug line-clamp-2">{matchName}</p>
                        {rep.match_date && <p className="text-gray-400 text-xs">{rep.match_date}</p>}
                        {qualityLevel && (
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border self-start ${QUALITY_BADGE_CLASS[qualityLevel] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                            {qualityLevel}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5">
      <Icon className={`w-5 h-5 ${color} mb-3`} />
      <p className="text-2xl font-bold text-navy-900">{value}</p>
      <p className="text-gray-500 text-sm mt-1">{label}</p>
    </div>
  );
}
